import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Paintbrush, MapPin, Mail, Euro, FileText, Copy, Check, Sparkles, Eye, ChevronDown, Gift } from "lucide-react";
import { useBrand } from "@/context/BrandContext";
import { BRAND_PRESETS } from "@/lib/brandPresets";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const PREVIEW_TARGETS = [
  { path: "/", label: "Homepage membres (B2C)", hint: "Vitrine partenaires interactive" },
  { path: "/pro", label: "Landing commerçants (B2B)", hint: "Page d'acquisition partenaires" },
  { path: "/devenir-partenaire", label: "Formulaire devenir partenaire", hint: "Soumission commerçant" },
  { path: "/admin/login", label: "Page de connexion admin", hint: "Login screen" },
];

const TABS = [
  { id: "presets", label: "Presets", icon: Sparkles },
  { id: "identity", label: "Identité", icon: FileText },
  { id: "colors", label: "Couleurs", icon: Paintbrush },
  { id: "geo", label: "Zone", icon: MapPin },
  { id: "contacts", label: "Contacts", icon: Mail },
  { id: "pricing", label: "Tarifs", icon: Euro },
  { id: "referral", label: "Parrainage", icon: Gift },
  { id: "texts", label: "Textes", icon: FileText },
  { id: "legal", label: "Légal", icon: FileText },
];

export default function AdminConfig() {
  const { refresh, startPreview } = useBrand();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("presets");
  const [copied, setCopied] = useState(false);
  const [appliedPreset, setAppliedPreset] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/config");
      setCfg(data);
    })();
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/config", cfg);
      await refresh();
      toast.success("Configuration enregistrée — rechargez l'app pour appliquer partout");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(cfg, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `brand.config.${(cfg.center_short || "site").replace(/[^a-z0-9]/gi, "_").toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("brand.config.json téléchargé");
  };

  const importJson = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        setCfg((c) => ({ ...c, ...data }));
        toast.success("Config importée — cliquez Enregistrer pour appliquer");
      } catch {
        toast.error("Fichier JSON invalide");
      }
    };
    reader.readAsText(file);
  };

  const copyTemplate = () => {
    const tpl = JSON.stringify(cfg, null, 2);
    navigator.clipboard.writeText(tpl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const applyPreset = (preset) => {
    setCfg((c) => ({ ...c, ...preset.config }));
    setAppliedPreset(preset.id);
    toast.success(`Preset "${preset.label}" appliqué — cliquez Enregistrer`);
  };

  const openPreview = (target = "/") => {
    startPreview(cfg);
    toast.success("Mode aperçu activé — ouverture du site public");
    navigate(target);
  };

  if (!cfg) return <div className="text-muted-foreground">Chargement…</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-config-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-navy-900/60 font-semibold">Configuration</div>
          <h1 className="font-heading font-extrabold text-3xl text-navy-900 mt-1">Brand CMS</h1>
          <p className="text-sm text-navy-900/60 mt-1">Personnalisez tout le site sans toucher au code. Concept Prodinvideo SPRL.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex rounded-full overflow-hidden border border-navy-900/15 bg-white">
            <button
              onClick={() => openPreview("/")}
              className="px-3 py-2 text-xs font-semibold flex items-center gap-1.5 hover:bg-navy-900/5 transition-colors"
              data-testid="preview-config-btn"
              title="Prévisualiser le preset appliqué sur la landing B2B"
            >
              <Eye className="w-4 h-4" /> Prévisualiser
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="px-2 border-l border-navy-900/10 hover:bg-navy-900/5 flex items-center transition-colors"
                  data-testid="preview-target-trigger"
                  title="Choisir la page d'aperçu"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel className="text-xs">Prévisualiser sur…</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {PREVIEW_TARGETS.map((t) => (
                  <DropdownMenuItem
                    key={t.path}
                    onClick={() => openPreview(t.path)}
                    className="flex-col items-start gap-0.5 cursor-pointer"
                    data-testid={`preview-target-${t.path.replace(/\//g, "-") || "root"}`}
                  >
                    <div className="text-sm font-semibold">{t.label}</div>
                    <div className="text-[11px] text-navy-900/60">{t.hint} · {t.path}</div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <button onClick={copyTemplate} className="btn-ghost text-xs" data-testid="copy-tpl">
            {copied ? <Check className="w-4 h-4 text-lime-600" /> : <Copy className="w-4 h-4" />} Copier le template
          </button>
          <button onClick={exportJson} className="btn-ghost text-xs" data-testid="export-config">Exporter .json</button>
          <label className="btn-ghost text-xs cursor-pointer">
            Importer .json
            <input type="file" accept="application/json" className="hidden" onChange={importJson} />
          </label>
          <button onClick={save} disabled={busy} className="btn-navy text-sm" data-testid="save-config">
            <Save className="w-4 h-4" /> {busy ? "…" : "Enregistrer"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-navy-900/10 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.id ? "border-lime-500 text-navy-900" : "border-transparent text-navy-900/50 hover:text-navy-900"}`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Panels */}
      {tab === "presets" && (
        <div className="space-y-4">
          <p className="text-sm text-navy-900/70 leading-relaxed">
            Choisissez une <strong>palette sectorielle</strong> pour démarrer. Les couleurs et les textes types sont pré-remplis.
            Ensuite, personnalisez dans les autres onglets (logo, ville, contacts, prix). Aucun preset n'écrase le logo ou les données identitaires.
            <br/>
            <span className="text-xs text-navy-900/60">Astuce : cliquez sur un preset pour l'appliquer, puis sur <strong>Prévisualiser</strong> (en haut) pour voir le rendu sur le site <em>sans enregistrer</em>.</span>
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {BRAND_PRESETS.map((p) => {
              const active = appliedPreset === p.id || cfg.color_accent === p.config.color_accent;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className={`group text-left rounded-2xl border-2 p-5 transition-all hover:-translate-y-0.5 ${active ? "border-lime-500 shadow-lg" : "border-navy-900/10 hover:border-navy-900/30"}`}
                  data-testid={`preset-${p.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-2xl">{p.emoji}</div>
                    <div className="flex gap-1.5">
                      {p.swatches.map((c) => (
                        <div key={c} className="w-6 h-6 rounded-full border border-white shadow" style={{ background: c }} />
                      ))}
                    </div>
                  </div>
                  <div className="font-heading font-bold text-navy-900 text-base">{p.label}</div>
                  <div className="text-xs text-navy-900/60 mt-1 leading-relaxed">{p.description}</div>
                  <div className="mt-4 rounded-lg p-2 text-xs" style={{ background: p.config.color_primary, color: p.config.color_accent }}>
                    Aperçu · <span style={{ color: p.config.color_accent }}>accent</span>
                  </div>
                  {active && <div className="mt-3 text-[10px] uppercase tracking-widest text-lime-700 font-bold">✓ Appliqué</div>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {tab === "identity" && (
        <Panel>
          <TextField label="Nom complet du centre" value={cfg.center_name} onChange={(v) => set("center_name", v)} id="center_name" />
          <TextField label="Nom court (logo / titre)" value={cfg.center_short} onChange={(v) => set("center_short", v)} id="center_short" />
          <TextField label="Slogan FR" value={cfg.tagline_fr} onChange={(v) => set("tagline_fr", v)} id="tagline_fr" />
          <TextField label="Slogan NL" value={cfg.tagline_nl} onChange={(v) => set("tagline_nl", v)} id="tagline_nl" />
          <TextField label="URL du logo (PNG transparent recommandé)" value={cfg.logo_url} onChange={(v) => set("logo_url", v)} id="logo_url" />
          {cfg.logo_url && <div className="col-span-full flex items-center gap-4"><div className="w-20 h-20 rounded-2xl bg-white border border-navy-900/10 p-2 flex items-center justify-center"><img src={cfg.logo_url} alt="preview" className="max-w-full max-h-full object-contain" /></div><span className="text-xs text-navy-900/60">Aperçu logo</span></div>}
          <TextField label="URL favicon (optionnel, .ico/.png 32×32)" value={cfg.favicon_url} onChange={(v) => set("favicon_url", v)} id="favicon_url" />
        </Panel>
      )}

      {tab === "colors" && (
        <Panel>
          <ColorField label="Couleur primaire (navy)" value={cfg.color_primary} onChange={(v) => set("color_primary", v)} id="color_primary" />
          <ColorField label="Primaire foncée (hover/bg)" value={cfg.color_primary_dark} onChange={(v) => set("color_primary_dark", v)} id="color_primary_dark" />
          <ColorField label="Couleur accent (lime)" value={cfg.color_accent} onChange={(v) => set("color_accent", v)} id="color_accent" />
          <ColorField label="Accent foncée (texte lime)" value={cfg.color_accent_dark} onChange={(v) => set("color_accent_dark", v)} id="color_accent_dark" />
          <div className="col-span-full p-5 rounded-2xl text-white relative" style={{ background: cfg.color_primary }}>
            <div className="text-[10px] uppercase tracking-widest opacity-70 mb-2">Aperçu</div>
            <div className="flex gap-3 items-center">
              <button className="px-5 py-2.5 rounded-full font-bold text-sm" style={{ background: cfg.color_accent, color: cfg.color_primary }}>Call to action</button>
              <span style={{ color: cfg.color_accent }} className="font-bold">Texte accent</span>
            </div>
          </div>
        </Panel>
      )}

      {tab === "geo" && (
        <Panel>
          <TextField label="Ville" value={cfg.city} onChange={(v) => set("city", v)} id="city" />
          <TextField label="Région" value={cfg.region} onChange={(v) => set("region", v)} id="region" />
          <TextField label="Pays" value={cfg.country} onChange={(v) => set("country", v)} id="country" />
          <TextField label="Adresse complète" value={cfg.address} onChange={(v) => set("address", v)} id="address" full />
        </Panel>
      )}

      {tab === "contacts" && (
        <Panel>
          <TextField label="Email principal" value={cfg.email} onChange={(v) => set("email", v)} id="email" type="email" />
          <TextField label="Téléphone" value={cfg.phone} onChange={(v) => set("phone", v)} id="phone" />
          <TextField label="Site web" value={cfg.website} onChange={(v) => set("website", v)} id="website" />
          <TextField label="Instagram" value={cfg.instagram} onChange={(v) => set("instagram", v)} id="instagram" />
          <TextField label="Facebook" value={cfg.facebook} onChange={(v) => set("facebook", v)} id="facebook" />
          <TextField label="LinkedIn" value={cfg.linkedin} onChange={(v) => set("linkedin", v)} id="linkedin" />
        </Panel>
      )}

      {tab === "pricing" && (
        <Panel>
          <NumberField label="Prix Plan Adult (€/mois)" value={cfg.plan_adult_price} onChange={(v) => set("plan_adult_price", v)} id="plan_adult_price" step="0.01" />
          <NumberField label="Prix Plan Junior (€/mois)" value={cfg.plan_junior_price} onChange={(v) => set("plan_junior_price", v)} id="plan_junior_price" step="0.01" />
          <TextField label="Label Adult FR" value={cfg.plan_adult_label_fr} onChange={(v) => set("plan_adult_label_fr", v)} id="plan_adult_label_fr" />
          <TextField label="Label Adult NL" value={cfg.plan_adult_label_nl} onChange={(v) => set("plan_adult_label_nl", v)} id="plan_adult_label_nl" />
          <TextField label="Label Junior FR" value={cfg.plan_junior_label_fr} onChange={(v) => set("plan_junior_label_fr", v)} id="plan_junior_label_fr" />
          <TextField label="Label Junior NL" value={cfg.plan_junior_label_nl} onChange={(v) => set("plan_junior_label_nl", v)} id="plan_junior_label_nl" />
        </Panel>
      )}

      {tab === "referral" && (
        <div className="space-y-5">
          <div className="rounded-2xl bg-lime-500/10 border border-lime-500/30 p-5 text-sm text-navy-900/80 leading-relaxed">
            <strong>Avantage parrainage réseau</strong> — Bloc affiché sur la landing B2B (après Tarifs) et la landing B2C (après simulateur). Il met en avant la remise offerte aux nouveaux membres apportés par un commerçant partenaire. <em>Désactivable et entièrement personnalisable par centre.</em>
          </div>
          <Panel>
            <div className="col-span-full">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!cfg.referral_enabled}
                  onChange={(e) => set("referral_enabled", e.target.checked)}
                  className="w-5 h-5 accent-lime-500"
                  data-testid="field-referral_enabled"
                />
                <span className="font-semibold text-navy-900">Activer l'avantage parrainage sur les landings</span>
              </label>
            </div>
            <NumberField label="Réduction (%)" value={cfg.referral_discount_pct} onChange={(v) => set("referral_discount_pct", v)} id="referral_discount_pct" step="0.1" />
            <NumberField label="Économie Adult (€/an)" value={cfg.referral_saving_adult} onChange={(v) => set("referral_saving_adult", v)} id="referral_saving_adult" step="1" />
            <NumberField label="Économie Junior/Senior (€/an)" value={cfg.referral_saving_junior} onChange={(v) => set("referral_saving_junior", v)} id="referral_saving_junior" step="1" />
            <TextField label="URL d'abonnement (CTA B2C)" value={cfg.referral_url} onChange={(v) => set("referral_url", v)} id="referral_url" full />
            <TextField label="Période FR (ex: « annuel »)" value={cfg.referral_period_label_fr} onChange={(v) => set("referral_period_label_fr", v)} id="referral_period_label_fr" />
            <TextField label="Période NL (ex: « jaarlijks »)" value={cfg.referral_period_label_nl} onChange={(v) => set("referral_period_label_nl", v)} id="referral_period_label_nl" />
            <TextAreaField label="Titre FR (override, vide = défaut)" value={cfg.referral_title_fr} onChange={(v) => set("referral_title_fr", v)} id="referral_title_fr" hint="Laissez vide pour utiliser le titre traduit par défaut." />
            <TextAreaField label="Titre NL (override, vide = défaut)" value={cfg.referral_title_nl} onChange={(v) => set("referral_title_nl", v)} id="referral_title_nl" />
            <TextAreaField label="Note conditions FR" value={cfg.referral_note_fr} onChange={(v) => set("referral_note_fr", v)} id="referral_note_fr" hint='Ex: "*Uniquement sur les abonnements annuels."' />
            <TextAreaField label="Note conditions NL" value={cfg.referral_note_nl} onChange={(v) => set("referral_note_nl", v)} id="referral_note_nl" />
          </Panel>
        </div>
      )}

      {tab === "texts" && (
        <Panel>
          <TextAreaField label="Titre hero FR (override)" value={cfg.hero_title_fr} onChange={(v) => set("hero_title_fr", v)} id="hero_title_fr" hint="Laisser vide pour utiliser le texte par défaut de la langue" />
          <TextAreaField label="Titre hero NL (override)" value={cfg.hero_title_nl} onChange={(v) => set("hero_title_nl", v)} id="hero_title_nl" />
          <TextAreaField label="Sous-titre hero FR" value={cfg.hero_subtitle_fr} onChange={(v) => set("hero_subtitle_fr", v)} id="hero_subtitle_fr" />
          <TextAreaField label="Sous-titre hero NL" value={cfg.hero_subtitle_nl} onChange={(v) => set("hero_subtitle_nl", v)} id="hero_subtitle_nl" />
        </Panel>
      )}

      {tab === "legal" && (
        <Panel>
          <TextField label="Propriétaire légal" value={cfg.legal_owner} onChange={(v) => set("legal_owner", v)} id="legal_owner" />
          <TextField label="URL propriétaire" value={cfg.legal_owner_url} onChange={(v) => set("legal_owner_url", v)} id="legal_owner_url" />
          <TextField label="Mention copyright (footer)" value={cfg.copyright_text} onChange={(v) => set("copyright_text", v)} id="copyright_text" full />
          <div className="col-span-full rounded-2xl bg-lime-500/10 border border-lime-500/30 p-4 text-xs text-navy-900/80 leading-relaxed">
            ⚠️ La mention Prodinvideo SPRL est légalement obligatoire (concept propriétaire). Ne la supprimez pas.
          </div>
        </Panel>
      )}
    </div>
  );
}

function Panel({ children }) {
  return <div className="grid md:grid-cols-2 gap-4">{children}</div>;
}

function TextField({ label, value, onChange, id, type = "text", full = false }) {
  return (
    <div className={full ? "col-span-full" : ""}>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} data-testid={`field-${id}`} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function NumberField({ label, value, onChange, id, step = "1" }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} data-testid={`field-${id}`} type="number" step={step} value={value ?? ""} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} />
    </div>
  );
}

function ColorField({ label, value, onChange, id }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2 items-center">
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="w-12 h-10 rounded cursor-pointer border border-navy-900/20" data-testid={`color-${id}`} />
        <Input id={id} value={value || ""} onChange={(e) => onChange(e.target.value)} className="font-mono text-xs" />
      </div>
    </div>
  );
}

function TextAreaField({ label, value, onChange, id, hint }) {
  return (
    <div className="col-span-full">
      <Label htmlFor={id}>{label}</Label>
      <Textarea id={id} data-testid={`field-${id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} />
      {hint && <p className="text-xs text-navy-900/50 mt-1">{hint}</p>}
    </div>
  );
}
